/**
 * Componente de verificación de contraseña reutilizable
 * Se utiliza antes de realizar acciones sensibles como cambiar email, contraseña o cerrar cuenta
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useVerifyPasswordMutation } from '../../hooks/useAuthMutations';

/**
 * Props del componente PasswordVerificationScreen
 * @param {object} props
 * @param {string} props.title - Título de la pantalla
 * @param {string} props.subtitle - Subtítulo descriptivo
 * @param {function} props.onSuccess - Función callback cuando la verificación es exitosa
 * @param {string} props.successMessage - Mensaje a mostrar en caso de éxito
 * @param {string} props.errorTitle - Título del mensaje de error
 * @param {string} props.buttonText - Texto del botón principal
 */
const PasswordVerificationScreen = ({
  title = 'Verificar tu contraseña',
  subtitle = 'Para continuar, vuelve a introducir tu contraseña de Amarsi.',
  onSuccess,
  successMessage = 'Verificación exitosa',
  errorTitle = 'Error de verificación',
  buttonText = 'Continuar'
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);

  // Hook para verificar la contraseña
  const verifyPasswordMutation = useVerifyPasswordMutation({
    onSuccess: () => {
      // Mostrar toast de éxito
      toast.success(successMessage);

      // Limpiar campo de contraseña
      setPassword('');

      // Ejecutar callback si se proporcionó
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      } else {
        // Si no hay callback, regresar a la pantalla anterior
        navigate(-1);
      }
    },
    onError: (error) => {
      // Mostrar toast de error
      toast.error(error.message || 'Contraseña incorrecta');
    }
  });

  /**
   * Maneja el proceso de verificación de contraseña
   */
  const handleVerifyPassword = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!password.trim()) {
      toast.error('Por favor, ingresa tu contraseña');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    // Ejecutar verificación
    verifyPasswordMutation.mutate(password);
  };

  /**
   * Maneja el caso de contraseña olvidada
   * (Comentado por ahora, disponible para uso futuro)
   */
  // const handleForgotPassword = () => {
  //   // Cerrar sesión y redirigir a login
  //   navigate('/login');
  // };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Card principal */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          {/* Botón de retroceso */}
          <button
            onClick={() => navigate(-1)}
            className="mb-6 text-gray-400 hover:text-white transition-colors"
            disabled={verifyPasswordMutation.isLoading}
          >
            <FiArrowLeft className="h-6 w-6" />
          </button>

          {/* Título principal */}
          <div className="text-center mb-2">
            <h2 className="text-2xl font-bold text-white">
              {title}
            </h2>
          </div>

          {/* Subtítulo descriptivo */}
          <div className="text-center mb-6">
            <p className="text-gray-400">
              {subtitle}
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleVerifyPassword} className="space-y-4">
            {/* Campo de entrada de contraseña */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tu contraseña
              </label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Ingresa tu contraseña"
                  autoFocus
                  disabled={verifyPasswordMutation.isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  disabled={verifyPasswordMutation.isLoading}
                >
                  {showPass ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Email del usuario */}
            <div className="text-center">
              <p className="text-sm text-gray-400">
                {user?.email}
              </p>
            </div>

            {/* Botón principal de verificación */}
            <button
              type="submit"
              disabled={password.length < 6 || verifyPasswordMutation.isLoading}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {verifyPasswordMutation.isLoading ? (
                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {buttonText}
            </button>
          </form>

          {/* Enlace de recuperación de contraseña (opcional, comentado por ahora) */}
          {/* <div className="text-center mt-4">
            <button
              onClick={handleForgotPassword}
              className="text-sm text-violet-400 hover:text-violet-300 transition-colors"
              disabled={verifyPasswordMutation.isLoading}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default PasswordVerificationScreen;
